import { useEffect, useState } from "react";
import {
  Box, Card, CardBody, Heading, HStack, VStack, Stack, Flex, Button, Text,
  Table, Thead, Tbody, Tr, Th, Td, Switch, Input, Spinner,
  IconButton, Select, useToast, Alert, AlertIcon, Badge,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, useDisclosure, Tag, TagLabel, TagCloseButton, Wrap, WrapItem, NumberInput,
  NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper,
  Tooltip, useBreakpointValue
} from "@chakra-ui/react";
import { FiPlus, FiTrash2, FiSave, FiClock, FiRefreshCw, FiInfo } from "react-icons/fi";
import {
  getConfiguracoesListaCompras,
  salvarHorariosListaCompras,
  salvarConfigFornecedorListaCompras,
  salvarWhatsappCompradorListaCompras,
  salvarWhatsappsPedidoFormalizado,
} from "../../services/api";

// Slots de 30 minutos: 00:00, 00:30, 01:00, ..., 23:30
const SLOTS_30MIN = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    out.push(`${String(h).padStart(2, "0")}:00`);
    out.push(`${String(h).padStart(2, "0")}:30`);
  }
  return out;
})();

// A tabela de fornecedores só aparece a partir daqui; abaixo disso vira cartão.
const BP_TABELA = "lg";

const labelDias = (n) => {
  if (n === 0) return "só hoje";
  if (n === 1) return "hoje + amanhã";
  return `hoje + ${n} dias`;
};

function HorariosEditor({ horarios, setHorarios }) {
  const toast = useToast();

  const usados = new Set(horarios.map((h) => h.horario));

  const adicionar = () => {
    const proximo = SLOTS_30MIN.find((s) => !usados.has(s));
    if (!proximo) {
      toast({ title: "Todos os horários já adicionados", status: "info", duration: 2000 });
      return;
    }
    const novo = [...horarios, { horario: proximo, dias_futuro: 0 }];
    novo.sort((a, b) => a.horario.localeCompare(b.horario));
    setHorarios(novo);
  };

  const remover = (idx) => {
    const novo = [...horarios];
    novo.splice(idx, 1);
    setHorarios(novo);
  };

  const trocarHorario = (idx, valor) => {
    if (usados.has(valor) && horarios[idx].horario !== valor) {
      toast({ title: "Horário duplicado", status: "warning", duration: 2000 });
      return;
    }
    const novo = [...horarios];
    novo[idx] = { ...novo[idx], horario: valor };
    novo.sort((a, b) => a.horario.localeCompare(b.horario));
    setHorarios(novo);
  };

  const trocarDias = (idx, valor) => {
    const n = Math.max(0, Math.min(7, Number(valor) || 0));
    const novo = [...horarios];
    novo[idx] = { ...novo[idx], dias_futuro: n };
    setHorarios(novo);
  };

  return (
    <VStack align="stretch" spacing={2}>
      {horarios.map((h, idx) => (
        // No celular a linha vira um cartãozinho: os 4 controles não cabem lado a lado.
        <Flex
          key={`${h.horario}-${idx}`}
          gap={2}
          align="center"
          wrap="wrap"
          borderWidth={{ base: "1px", md: "0" }}
          borderRadius="md"
          p={{ base: 2, md: 0 }}
        >
          <Select
            value={h.horario}
            onChange={(e) => trocarHorario(idx, e.target.value)}
            w="110px"
            flexShrink={0}
            size="sm"
          >
            {SLOTS_30MIN.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Select>

          <Tooltip label="Quantos dias à frente este horário inclui (0 = só itens com envio até hoje; 1 = até amanhã)" hasArrow>
            <HStack spacing={1} flexShrink={0}>
              <Text fontSize="xs" color="gray.600">+</Text>
              <NumberInput
                size="sm"
                min={0}
                max={7}
                value={h.dias_futuro ?? 0}
                onChange={(v) => trocarDias(idx, v)}
                w="80px"
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="xs" color="gray.600">dia(s)</Text>
            </HStack>
          </Tooltip>

          <Badge colorScheme={h.dias_futuro > 0 ? "orange" : "gray"} fontSize="10px">
            {labelDias(h.dias_futuro ?? 0)}
          </Badge>

          <IconButton
            aria-label="Remover horário"
            icon={<FiTrash2 />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={() => remover(idx)}
            ml="auto"
          />
        </Flex>
      ))}
      <Box>
        <Button leftIcon={<FiPlus />} size="sm" onClick={adicionar} w={{ base: "100%", md: "auto" }}>
          Adicionar horário
        </Button>
      </Box>
    </VStack>
  );
}

function ModalHorariosFornecedor({ isOpen, onClose, fornecedor, horariosGlobais, onSalvar, saving }) {
  const [horarios, setHorarios] = useState([]);
  const [usaGlobal, setUsaGlobal] = useState(true);
  // size responsivo direto no Modal vazaria o my/minH/borderRadius do "full" para o
  // desktop (os breakpoints maiores só trocam o maxW); aqui só um size fica ativo.
  const tamanhoModal = useBreakpointValue({ base: "full", md: "lg" }, { ssr: false }) || "full";

  useEffect(() => {
    if (!fornecedor) return;
    const proprios = Array.isArray(fornecedor.horarios_lista_compras) ? fornecedor.horarios_lista_compras : [];
    setHorarios(proprios);
    setUsaGlobal(proprios.length === 0);
  }, [fornecedor, isOpen]);

  if (!fornecedor) return null;

  const handleSalvar = () => {
    onSalvar(usaGlobal ? [] : horarios);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={tamanhoModal} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="#01695E" fontSize={{ base: "md", md: "lg" }} pr={12}>
          Horários — {fornecedor.nome}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={{ base: 3, md: 6 }}>
          <VStack align="stretch" spacing={4}>
            <Alert status="info" borderRadius="md" fontSize="sm">
              <AlertIcon />
              <Box>
                <Text>
                  Quando "Usar horários globais" estiver ligado, o fornecedor herda os horários da configuração global.
                  Desligue para configurar horários específicos deste fornecedor.
                </Text>
                {horariosGlobais?.length > 0 && (
                  <Wrap mt={2} spacing={1}>
                    <WrapItem><Text fontSize="xs" color="gray.600">Globais:</Text></WrapItem>
                    {horariosGlobais.map((h, i) => (
                      <WrapItem key={`${h.horario}-${i}`}>
                        <Tag size="sm" colorScheme={h.dias_futuro > 0 ? "orange" : "gray"}>
                          {h.horario} ({labelDias(h.dias_futuro ?? 0)})
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}
              </Box>
            </Alert>

            <HStack>
              <Switch
                colorScheme="green"
                isChecked={usaGlobal}
                onChange={(e) => {
                  const novo = e.target.checked;
                  setUsaGlobal(novo);
                  if (novo) setHorarios([]);
                }}
              />
              <Text>Usar horários globais</Text>
            </HStack>

            {!usaGlobal && (
              <Box>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  Horários específicos deste fornecedor (slots de 30 min; cada um com offset de dias):
                </Text>
                <HorariosEditor horarios={horarios} setHorarios={setHorarios} />
                {horarios.length === 0 && (
                  <Alert status="warning" mt={3} borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    Sem horários específicos cadastrados — ao salvar, o fornecedor passa a usar os globais.
                  </Alert>
                )}
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Stack
            direction={{ base: "column-reverse", sm: "row" }}
            spacing={3}
            w={{ base: "100%", sm: "auto" }}
          >
            <Button variant="ghost" onClick={onClose} w={{ base: "100%", sm: "auto" }}>Cancelar</Button>
            <Button
              leftIcon={<FiSave />}
              colorScheme="green"
              bg="#01695E"
              onClick={handleSalvar}
              isLoading={saving}
              w={{ base: "100%", sm: "auto" }}
            >
              Salvar horários
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function ConfiguracoesListaCompras() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [horarios, setHorarios] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [savingHorarios, setSavingHorarios] = useState(false);
  const [whatsappComprador, setWhatsappComprador] = useState("");
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  const [whatsappsPedido, setWhatsappsPedido] = useState([]);
  const [novoWhatsappPedido, setNovoWhatsappPedido] = useState("");
  const [savingWhatsappsPedido, setSavingWhatsappsPedido] = useState(false);
  const [savingFornecedor, setSavingFornecedor] = useState({});
  const [savingHorariosFornecedor, setSavingHorariosFornecedor] = useState(false);
  const [fornecedorAtivoModal, setFornecedorAtivoModal] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const carregar = async () => {
    setLoading(true);
    const data = await getConfiguracoesListaCompras();
    if (data) {
      setHorarios(Array.isArray(data.horarios_geracao) ? data.horarios_geracao : []);
      setWhatsappComprador(data.whatsapp_comprador || "");
      setWhatsappsPedido(Array.isArray(data.whatsapps_pedido_formalizado) ? data.whatsapps_pedido_formalizado : []);
      setFornecedores(Array.isArray(data.fornecedores) ? data.fornecedores : []);
    }
    setLoading(false);
  };

  useEffect(() => {
    carregar();
  }, []);

  const salvarHorarios = async () => {
    setSavingHorarios(true);
    const r = await salvarHorariosListaCompras(horarios);
    setSavingHorarios(false);
    if (r && r.horarios_geracao) {
      setHorarios(r.horarios_geracao);
      toast({ title: "Horários salvos", status: "success", duration: 2500 });
    } else {
      const msg = r?.errors ? Object.values(r.errors).flat().join(" ") : "Erro ao salvar horários";
      toast({ title: msg, status: "error", duration: 4000 });
    }
  };

  const salvarWhatsapp = async () => {
    setSavingWhatsapp(true);
    const r = await salvarWhatsappCompradorListaCompras(whatsappComprador || null);
    setSavingWhatsapp(false);
    if (r && !r.errors) {
      setWhatsappComprador(r.whatsapp_comprador || "");
      toast({ title: "WhatsApp do comprador salvo", status: "success", duration: 2500 });
    } else {
      const msg = r?.errors ? Object.values(r.errors).flat().join(" ") : "Erro ao salvar WhatsApp do comprador";
      toast({ title: msg, status: "error", duration: 4000 });
    }
  };

  const adicionarWhatsappPedido = () => {
    const num = novoWhatsappPedido.trim();
    if (!/^\d{10,15}$/.test(num)) {
      toast({ title: "Número inválido — apenas dígitos (DDI+DDD+número), 10 a 15 dígitos", status: "warning", duration: 3000 });
      return;
    }
    if (whatsappsPedido.includes(num)) {
      toast({ title: "Número já adicionado", status: "info", duration: 2000 });
      return;
    }
    setWhatsappsPedido([...whatsappsPedido, num]);
    setNovoWhatsappPedido("");
  };

  const removerWhatsappPedido = (num) => {
    setWhatsappsPedido(whatsappsPedido.filter((n) => n !== num));
  };

  const salvarWhatsappsPedido = async () => {
    setSavingWhatsappsPedido(true);
    const r = await salvarWhatsappsPedidoFormalizado(whatsappsPedido);
    setSavingWhatsappsPedido(false);
    if (r && !r.errors) {
      setWhatsappsPedido(Array.isArray(r.whatsapps_pedido_formalizado) ? r.whatsapps_pedido_formalizado : []);
      toast({ title: "Números de notificação salvos", status: "success", duration: 2500 });
    } else {
      const msg = r?.errors ? Object.values(r.errors).flat().join(" ") : "Erro ao salvar números de notificação";
      toast({ title: msg, status: "error", duration: 4000 });
    }
  };

  const atualizarFornecedorLocal = (id, campo, valor) => {
    setFornecedores((arr) => arr.map((f) => (f.id === id ? { ...f, [campo]: valor } : f)));
  };

  const salvarFornecedor = async (forn) => {
    setSavingFornecedor((s) => ({ ...s, [forn.id]: true }));
    const payload = {
      ativo_lista_compras: !!forn.ativo_lista_compras,
      whatsapp_lista_compras: forn.whatsapp_lista_compras || null,
    };
    const r = await salvarConfigFornecedorListaCompras(forn.id, payload);
    setSavingFornecedor((s) => ({ ...s, [forn.id]: false }));
    if (r && r.fornecedor) {
      toast({ title: `${forn.nome} atualizado`, status: "success", duration: 2000 });
    } else {
      const msg = r?.errors ? Object.values(r.errors).flat().join(" ") : "Erro ao salvar fornecedor";
      toast({ title: msg, status: "error", duration: 4000 });
    }
  };

  const abrirHorariosFornecedor = (forn) => {
    setFornecedorAtivoModal(forn);
    onOpen();
  };

  const salvarHorariosFornecedor = async (novosHorarios) => {
    if (!fornecedorAtivoModal) return;
    setSavingHorariosFornecedor(true);
    const payload = {
      ativo_lista_compras: !!fornecedorAtivoModal.ativo_lista_compras,
      whatsapp_lista_compras: fornecedorAtivoModal.whatsapp_lista_compras || null,
      horarios_lista_compras: novosHorarios,
    };
    const r = await salvarConfigFornecedorListaCompras(fornecedorAtivoModal.id, payload);
    setSavingHorariosFornecedor(false);
    if (r && r.fornecedor) {
      atualizarFornecedorLocal(fornecedorAtivoModal.id, "horarios_lista_compras", r.fornecedor.horarios_lista_compras || []);
      toast({ title: `Horários de ${fornecedorAtivoModal.nome} atualizados`, status: "success", duration: 2500 });
      onClose();
    } else {
      const msg = r?.errors ? Object.values(r.errors).flat().join(" ") : "Erro ao salvar horários do fornecedor";
      toast({ title: msg, status: "error", duration: 4000 });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="300px">
        <VStack spacing={4}>
          <Spinner size="xl" color="#F68633" thickness="4px" />
          <Text color="#01695E">Carregando configurações...</Text>
        </VStack>
      </Box>
    );
  }

  const botaoHorarios = (f, horariosProprios, usaGlobal, largura) => (
    <Button
      size="sm"
      variant="outline"
      w={largura}
      leftIcon={usaGlobal ? <FiRefreshCw /> : <FiClock />}
      colorScheme={usaGlobal ? "gray" : "orange"}
      onClick={() => abrirHorariosFornecedor(f)}
    >
      {usaGlobal
        ? "Usa global"
        : `${horariosProprios.length} horário${horariosProprios.length === 1 ? "" : "s"}`}
    </Button>
  );

  return (
    <VStack spacing={6} align="stretch">
      {/* Horários globais */}
      <Card boxShadow="md">
        <CardBody px={{ base: 3, md: 5 }}>
          <Stack
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            spacing={2}
            mb={3}
          >
            <VStack align="start" spacing={0}>
              <Heading size="md" color="#01695E">Horários globais de geração</Heading>
              <Text fontSize="sm" color="gray.600">
                Slots de 30 minutos. Cada horário pode incluir itens de dias futuros (offset). Ex: 17:00 com offset 1 inclui itens com envio hoje OU amanhã.
              </Text>
            </VStack>
            <Badge colorScheme="orange" fontSize="md" px={3} py={1} flexShrink={0}>
              {horarios.length} horário{horarios.length === 1 ? "" : "s"}
            </Badge>
          </Stack>

          {horarios.length === 0 && (
            <Alert status="warning" borderRadius="md" mb={3} fontSize="sm">
              <AlertIcon />
              Nenhum horário global — fornecedores sem horários próprios não terão lista gerada.
            </Alert>
          )}

          <Alert status="info" borderRadius="md" mb={3} fontSize="xs">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Como funciona o offset de dias:</Text>
              <Text>• <b>0 (só hoje)</b>: lista só pega itens com envio até hoje</Text>
              <Text>• <b>1 (hoje + amanhã)</b>: o último horário do dia pode adiantar itens do dia seguinte</Text>
            </Box>
          </Alert>

          <HorariosEditor horarios={horarios} setHorarios={setHorarios} />

          <Button
            mt={4}
            leftIcon={<FiSave />}
            colorScheme="green"
            bg="#01695E"
            size="sm"
            w={{ base: "100%", md: "auto" }}
            onClick={salvarHorarios}
            isLoading={savingHorarios}
          >
            Salvar horários globais
          </Button>
        </CardBody>
      </Card>

      {/* Aviso ao comprador via WhatsApp quando a lista é totalmente respondida */}
      <Card boxShadow="md">
        <CardBody px={{ base: 3, md: 5 }}>
          <VStack align="stretch" spacing={3}>
            <VStack align="start" spacing={0}>
              <Heading size="md" color="#01695E">Aviso ao comprador</Heading>
              <Text fontSize="sm" color="gray.600">
                WhatsApp que recebe o aviso automático quando uma lista é totalmente respondida pelo fornecedor.
              </Text>
            </VStack>
            <Box>
              <Text fontSize="sm" mb={1} fontWeight="bold">WhatsApp do comprador</Text>
              <Input
                size="sm"
                inputMode="numeric"
                maxW={{ base: "100%", md: "260px" }}
                placeholder="5511941524136"
                value={whatsappComprador}
                onChange={(e) => setWhatsappComprador(e.target.value.replace(/\D/g, ""))}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Apenas dígitos: DDI + DDD + número. Deixe vazio para desativar o aviso.
              </Text>
            </Box>
            <Button
              leftIcon={<FiSave />}
              colorScheme="green"
              bg="#01695E"
              size="sm"
              alignSelf={{ base: "stretch", md: "flex-start" }}
              onClick={salvarWhatsapp}
              isLoading={savingWhatsapp}
            >
              Salvar WhatsApp do comprador
            </Button>
          </VStack>
        </CardBody>
      </Card>

      {/* Notificação de pedido formalizado pelo fornecedor */}
      <Card boxShadow="md">
        <CardBody px={{ base: 3, md: 5 }}>
          <VStack align="stretch" spacing={3}>
            <VStack align="start" spacing={0}>
              <Heading size="md" color="#01695E">Notificação de pedido formalizado</Heading>
              <Text fontSize="sm" color="gray.600">
                WhatsApps que recebem o aviso quando o fornecedor informa o número do pedido formalizado na página pública da lista.
              </Text>
            </VStack>

            {whatsappsPedido.length === 0 ? (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                Nenhum número cadastrado — ninguém será avisado quando o fornecedor informar o pedido.
              </Alert>
            ) : (
              <Wrap spacing={2}>
                {whatsappsPedido.map((num) => (
                  <WrapItem key={num}>
                    <Tag size="lg" colorScheme="green" borderRadius="full">
                      <TagLabel>{num}</TagLabel>
                      <TagCloseButton onClick={() => removerWhatsappPedido(num)} />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}

            <Stack direction={{ base: "column", sm: "row" }} spacing={2}>
              <Input
                size="sm"
                inputMode="numeric"
                maxW={{ base: "100%", sm: "260px" }}
                placeholder="5511941524136"
                value={novoWhatsappPedido}
                onChange={(e) => setNovoWhatsappPedido(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => { if (e.key === 'Enter') adicionarWhatsappPedido(); }}
              />
              <Button leftIcon={<FiPlus />} size="sm" onClick={adicionarWhatsappPedido} flexShrink={0}>
                Adicionar
              </Button>
            </Stack>
            <Text fontSize="xs" color="gray.500">
              Apenas dígitos: DDI + DDD + número. Lembre de salvar após adicionar ou remover.
            </Text>

            <Button
              leftIcon={<FiSave />}
              colorScheme="green"
              bg="#01695E"
              size="sm"
              alignSelf={{ base: "stretch", md: "flex-start" }}
              onClick={salvarWhatsappsPedido}
              isLoading={savingWhatsappsPedido}
            >
              Salvar números de notificação
            </Button>
          </VStack>
        </CardBody>
      </Card>

      {/* Fornecedores */}
      <Card boxShadow="md">
        <CardBody px={{ base: 3, md: 5 }}>
          <Heading size="md" color="#01695E" mb={3}>Fornecedores</Heading>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Ative ou desative cada fornecedor, configure o WhatsApp e (opcionalmente) horários próprios. Sem horários próprios, o fornecedor usa os globais.
          </Text>

          {/* Desktop: tabela. */}
          <Box display={{ base: "none", [BP_TABELA]: "block" }} overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Fornecedor</Th>
                  <Th w="60px">Ativo</Th>
                  <Th minW="200px">WhatsApp</Th>
                  <Th w="130px">Horários</Th>
                  <Th w="100px"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {fornecedores.map((f) => {
                  const horariosProprios = Array.isArray(f.horarios_lista_compras) ? f.horarios_lista_compras : [];
                  const usaGlobal = horariosProprios.length === 0;
                  return (
                    <Tr key={f.id} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight="bold">{f.nome}</Td>
                      <Td>
                        <Switch
                          colorScheme="green"
                          isChecked={!!f.ativo_lista_compras}
                          onChange={(e) => atualizarFornecedorLocal(f.id, "ativo_lista_compras", e.target.checked)}
                        />
                      </Td>
                      <Td>
                        <Input
                          size="sm"
                          inputMode="numeric"
                          placeholder="5511941524136"
                          value={f.whatsapp_lista_compras || ""}
                          onChange={(e) => atualizarFornecedorLocal(f.id, "whatsapp_lista_compras", e.target.value.replace(/\D/g, ""))}
                        />
                      </Td>
                      <Td>{botaoHorarios(f, horariosProprios, usaGlobal, "auto")}</Td>
                      <Td>
                        <Button
                          size="sm"
                          leftIcon={<FiSave />}
                          colorScheme="green"
                          bg="#01695E"
                          onClick={() => salvarFornecedor(f)}
                          isLoading={!!savingFornecedor[f.id]}
                        >
                          Salvar
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {/* Telas estreitas: cartões. Na tabela o campo de WhatsApp fica ilegível. */}
          <Stack display={{ base: "flex", [BP_TABELA]: "none" }} spacing={3}>
            {fornecedores.map((f) => {
              const horariosProprios = Array.isArray(f.horarios_lista_compras) ? f.horarios_lista_compras : [];
              const usaGlobal = horariosProprios.length === 0;
              return (
                <Box key={f.id} borderWidth="1px" borderRadius="md" p={3}>
                  <Flex justify="space-between" align="center" gap={3} mb={3}>
                    <Text fontWeight="bold" noOfLines={2}>{f.nome}</Text>
                    <HStack spacing={2} flexShrink={0}>
                      <Text fontSize="xs" color="gray.600">Ativo</Text>
                      <Switch
                        colorScheme="green"
                        isChecked={!!f.ativo_lista_compras}
                        onChange={(e) => atualizarFornecedorLocal(f.id, "ativo_lista_compras", e.target.checked)}
                      />
                    </HStack>
                  </Flex>

                  <Text fontSize="11px" color="gray.500" fontWeight="semibold" mb={1}>WhatsApp</Text>
                  <Input
                    size="sm"
                    inputMode="numeric"
                    placeholder="5511941524136"
                    value={f.whatsapp_lista_compras || ""}
                    onChange={(e) => atualizarFornecedorLocal(f.id, "whatsapp_lista_compras", e.target.value.replace(/\D/g, ""))}
                  />

                  <Stack direction={{ base: "column", sm: "row" }} spacing={2} mt={3}>
                    {botaoHorarios(f, horariosProprios, usaGlobal, { base: "100%", sm: "auto" })}
                    <Button
                      size="sm"
                      flex={{ base: "none", sm: "1" }}
                      leftIcon={<FiSave />}
                      colorScheme="green"
                      bg="#01695E"
                      onClick={() => salvarFornecedor(f)}
                      isLoading={!!savingFornecedor[f.id]}
                    >
                      Salvar
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </CardBody>
      </Card>

      <ModalHorariosFornecedor
        isOpen={isOpen}
        onClose={onClose}
        fornecedor={fornecedorAtivoModal}
        horariosGlobais={horarios}
        onSalvar={salvarHorariosFornecedor}
        saving={savingHorariosFornecedor}
      />
    </VStack>
  );
}
